import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';

import { FinanceSelect } from '../../../../financeProviders/shared/FinanceSelect';
import { Label } from '../../../../../Toolkit/Inputs/Label';
import { Spacer } from '../../../../../../helpers/genericStyles';
import { FinancePriceField } from '../../../../financeProviders/shared/PriceField/PriceField';
import { FinanceTextField } from '../../../../financeProviders/shared/FinanceTextField/FinanceTextField';
import { ErrorMessage } from '../../../../../Toolkit/Inputs/ErrorMessage';
import { FinanceRadioButton } from '../../../../financeProviders/shared/FinanceRadio/FinanceRadio';

import { type CloseBrothersForm } from '../../../../queries/schema/carFinance.schema';

const RadioButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.M24};
`;

const ErrorMessageContainer = styled.div`
  height: ${({ theme }) => theme.spacing.M16};
`;

const BANKS = [
  {
    label: 'Select your primary bank',
    value: '',
  },
  'Allied Irish Banks',
  'An Post',
  'Bank of Ireland',
  'Credit Union',
  'EBS',
  'N26',
  'Permanent TSB',
  'Revolut',
];

const ACCOUNT_TYPES = {
  INDIVIDUAL_ACCOUNT: 'Individual account',
  JOINT_ACCOUNT: 'Joint account',
} as const;

const YEARS_WITH_BANK = [
  { value: '', label: 'Select years with bank' },
  { value: '0', label: 'Less than 1 year' },
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
  { value: '6', label: '6 years' },
  { value: '7', label: '7 years' },
  { value: '8', label: '8 years' },
  { value: '9', label: '9 years' },
  { value: '10 or more', label: '10+ years' },
];

const BankingInformation = () => {
  const { control } = useFormContext<CloseBrothersForm>();

  const content = (
    <>
      <Spacer marginBottom={['M16', 'M24']} />
      <Label htmlFor="bankingDetails.income">Monthly income after tax*</Label>
      <Controller
        control={control}
        name="bankingDetails.income"
        render={({ field, fieldState }) => (
          <FinancePriceField {...field} {...fieldState} />
        )}
      />
      <Spacer marginBottom="S4" />
      <Label htmlFor="bankingDetails.monthlyMortgageRent">
        Monthly mortgage/Rent payments*
      </Label>
      <Controller
        control={control}
        name="bankingDetails.monthlyMortgageRent"
        render={({ field, fieldState }) => (
          <FinancePriceField {...field} {...fieldState} />
        )}
      />
      <Spacer marginBottom="S4" />
      <Label htmlFor="bankingDetails.bankName">
        Who do you normally bank with?*
      </Label>
      <Controller
        control={control}
        name="bankingDetails.bankName"
        render={({ field, fieldState }) => (
          <FinanceSelect
            {...field}
            {...fieldState}
            placeholderAsOption
            options={BANKS}
          />
        )}
      />
      <Spacer marginBottom="S4" />
      <Label htmlFor="bankingDetails.accountType">Account type*</Label>
      <Controller
        control={control}
        name="bankingDetails.accountType"
        render={({ field, fieldState }) => (
          <>
            <RadioButtonGroup>
              <FinanceRadioButton
                {...field}
                {...fieldState}
                label={ACCOUNT_TYPES.INDIVIDUAL_ACCOUNT}
                value={ACCOUNT_TYPES.INDIVIDUAL_ACCOUNT}
              />
              <FinanceRadioButton
                {...field}
                {...fieldState}
                label={ACCOUNT_TYPES.JOINT_ACCOUNT}
                value={ACCOUNT_TYPES.JOINT_ACCOUNT}
              />
            </RadioButtonGroup>
            <ErrorMessageContainer>
              {fieldState.invalid && fieldState.error?.message && (
                <ErrorMessage text={fieldState.error?.message} />
              )}
            </ErrorMessageContainer>
          </>
        )}
      />
      <Spacer marginBottom="S4" />
      <Label htmlFor="bankingDetails.accountHolder">Account holder name</Label>
      <Controller
        control={control}
        name="bankingDetails.accountHolder"
        render={({ field, fieldState }) => (
          <FinanceTextField
            {...field}
            {...fieldState}
            isOptional
            placeholder="Enter account holder name"
            helpText="If different to applicant, or a joint account"
          />
        )}
      />
      <Spacer marginBottom="S12" />
      <Label htmlFor="bankingDetails.yearsWithBank">Years with bank*</Label>
      <Controller
        control={control}
        name="bankingDetails.yearsWithBank"
        render={({ field, fieldState }) => (
          <FinanceSelect
            {...field}
            {...fieldState}
            placeholderAsOption
            options={YEARS_WITH_BANK}
          />
        )}
      />
    </>
  );

  return content;
};

export { BankingInformation };
